/*/=======================================================================================\*\
|| IMPORTS DE DEPENDENCIAS                                                                 ||
\*\=======================================================================================/*/
import { useState, useRef, useEffect } from 'react';
import { ROUTES } from '../../const/routes';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { useTranslation } from 'react-i18next';

/*/=======================================================================================\*\
|| IMPORTS DE COMPONENTES PROPIOS DEL PROYECTO                                             ||
\*\=======================================================================================/*/
import styles from './ResetContrasenia.module.css';
import BannerLogo from '../../components/BannerLogo/BannerLogo';
import ImagenTragamonedasInicio from '../../components/ImagenTragamonedasInicio/ImagenTragamonedasInicio';
import BotonGrandeDinamico from '../../components/Botones/BotonGrandeDinamico/BotonGrandeDinamico';
import Footer from '../../components/Footer/Footer';

/*/=======================================================================================\*\
|| PÁGINA RESET CONTRASEÑA                                                                 ||
\*\=======================================================================================/*/
const ResetContrasenia = () => {
  const { t } = useTranslation();
  const [paso, setPaso] = useState(1); // 1 = ingresar código, 2 = nueva contraseña
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [nuevaContrasenia, setNuevaContrasenia] = useState('');
  const [confirmarContrasenia, setConfirmarContrasenia] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserData } = useUser();

  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      navigate(ROUTES.olvidoContrasenia);
    }
  }, [email, navigate]);

  // Manejar cambio en cada input de dígito
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCodigo = [...codigo];
    newCodigo[index] = value.slice(-1);
    setCodigo(newCodigo);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    const newCodigo = [...codigo];
    for (let i = 0; i < pastedData.length; i++) {
      newCodigo[i] = pastedData[i];
    }
    setCodigo(newCodigo);
    const nextEmpty = newCodigo.findIndex((d) => d === '');
    inputRefs.current[nextEmpty !== -1 ? nextEmpty : 5]?.focus();
  };

  // Paso 1: Verificar código
  const handleVerifyCode = async () => {
    const codigoStr = codigo.join('');
    if (codigoStr.length !== 6) {
      setError(t('resetPassword.codeRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_DIRECCION}/api/auth/verify-reset-code`,
        { email, codigo: codigoStr },
        { withCredentials: true }
      );

      setPaso(2);
    } catch (err) {
      if (err.response?.data?.msg) {
        setError(err.response.data.msg);
      } else {
        setError(t('resetPassword.errorChanging'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: Cambiar contraseña
  const handleResetPassword = async () => {
    setError(null);

    if (nuevaContrasenia.length < 6) {
      setError(t('resetPassword.passwordMinLength'));
      return;
    }

    if (nuevaContrasenia !== confirmarContrasenia) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_DIRECCION}/api/auth/reset-password`,
        {
          email,
          codigo: codigo.join(''),
          nuevaContrasenia,
        },
        { withCredentials: true }
      );

      setSuccess(true);
      await refreshUserData();
      setTimeout(() => {
        navigate(ROUTES.listaJuegos);
      }, 2000);
    } catch (err) {
      if (err.response?.data?.msg) {
        setError(err.response.data.msg);
      } else {
        setError(t('resetPassword.errorChanging'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Reenviar código
  const handleResend = async () => {
    setResendLoading(true);
    setResendMsg(null);
    setError(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_DIRECCION}/api/auth/forgot-password`,
        { email },
        { withCredentials: true }
      );
      setResendMsg(t('resetPassword.resendSuccess'));
      setCodigo(['', '', '', '', '', '']);
    } catch (err) {
      setError(t('resetPassword.resendError'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div
      className={
        'flex flex-col min-h-screen ' + styles.resetContraseniaContainer
      }
    >
      <div className="flex flex-col flex-grow items-center justify-between text-center px-4">
        <BannerLogo />

        <div className="flex flex-col lg:flex-row justify-center items-center lg:items-end w-full max-w-5xl gap-4 lg:gap-8 mb-4">
          <div className="w-full lg:w-auto flex justify-center order-1 lg:order-1">
            <ImagenTragamonedasInicio />
          </div>

          <div
            className={`p-5 lg:p-6 w-full max-w-md lg:max-w-xl space-y-3 flex flex-col bg-bgContenedorOscuro rounded-lg
                      border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro 
                      shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)] order-2 lg:order-2`}
          >
            <h2 className="text-xl text-yellow-500 font-bold mb-2">
              {paso === 1 ? t('resetPassword.step1Title') : t('resetPassword.step2Title')}
            </h2>

            {paso === 1 ? (
              <>
                <p className="text-sm text-gray-300 mb-2">
                  {t('resetPassword.codeInstructions1')}
                  <br />
                  {t('resetPassword.codeInstructions2')}{' '}
                  <span className="text-yellow-400 font-semibold">{email}</span>
                </p>

                <div
                  className="flex justify-center gap-2 mb-4"
                  onPaste={handlePaste}
                >
                  {codigo.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-11 h-12 text-center text-xl font-bold text-gray-900 bg-gray-200 rounded-lg border-2 border-gray-300 focus:border-indigo-600 focus:outline-none"
                    />
                  ))}
                </div>

                <BotonGrandeDinamico
                  texto={loading ? t('resetPassword.verifying') : t('resetPassword.verifyButton')}
                  bgColor="green"
                  funcion={handleVerifyCode}
                />

                <button
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="text-blue-400 hover:underline text-sm mt-2 disabled:opacity-50"
                >
                  {resendLoading ? t('resetPassword.resending') : t('resetPassword.resendCode')}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-300 mb-2">
                  {t('resetPassword.newPasswordInstructions')}
                </p>

                <div className="mb-3">
                  <input
                    type="password"
                    value={nuevaContrasenia}
                    onChange={(e) => setNuevaContrasenia(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 border-gray-300 focus:border-indigo-600 focus:outline-none"
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                  />
                </div>

                <div className="mb-3">
                  <input
                    type="password"
                    value={confirmarContrasenia}
                    onChange={(e) => setConfirmarContrasenia(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 border-gray-300 focus:border-indigo-600 focus:outline-none"
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                  />
                </div>

                <BotonGrandeDinamico
                  texto={loading ? t('resetPassword.changing') : t('resetPassword.changeButton')}
                  bgColor="green"
                  funcion={handleResetPassword}
                />
              </>
            )}

            <div className="mt-3 h-5">
              {success && (
                <p className="text-green-400 text-xs">
                  {t('resetPassword.successMessage')}
                </p>
              )}
              {error && <p className="text-red-400 text-xs">{error}</p>}
              {resendMsg && (
                <p className="text-green-400 text-xs">{resendMsg}</p>
              )}
            </div>

            <button
              onClick={() => navigate(ROUTES.login)}
              className="text-blue-400 hover:underline text-sm"
            >
              {t('forgotPassword.backToLogin')}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
};
export default ResetContrasenia;
